/**
 * VoiceControlsBar — main control row for VoicePanel.
 *
 * Renders the in-line voice controls: state-driven orb + live mic level
 * meter + status label + PTT button (click-toggle OR push-to-talk) + TTS
 * text input + Speak button + dispatch action display + transcript +
 * streaming overlay (Phase 9.6) + stop button (Phase 9.7) + history
 * toggle + settings button + error cards (lockdown/iOS-permission/raw).
 *
 * Reads its own slice of useVoiceStore + useUIStore directly. Owns local
 * `ttsText` state. Receives `historyOpen` + `setHistoryOpen` from parent
 * (shared with sibling VoiceHistoryLog mount-gate) + `sectionRef` (parent
 * focus-panel listener target) + `lockdownLikely` (parent computes once
 * at mount via isLockdownLikelyBlocked).
 *
 * Extracted from VoicePanel.tsx as part of DESHITTIFICATION Phase 3
 * (2026-05-26). Smart-subcomponent pattern.
 */
import { useState, type MutableRefObject } from 'react';
import {
  isLikelyIosPermissionDenied,
  useVoiceStore,
} from '@/stores/voiceStore';
import { useUIStore } from '@/stores/uiStore';
import { compactVoiceHistory } from '@/services/voiceCompaction';
import { VoiceSettingsIcon } from './VoiceSettingsIcon';
import { MicLockdownCard } from './MicLockdownCard';
import { MicPermissionDeniedCard } from './MicPermissionDeniedCard';

const STATUS_LABEL: Record<string, string> = {
  idle: 'standby',
  // W (2026-05-16) — distinct user-facing label so the user knows to wait
  // before speaking. The WS handshake on the VPS reverse-tunnel path has been
  // observed taking up to 15 seconds; pre-W, status flipped straight to
  // 'recording' and the user's first 0-15s of speech was lost.
  connecting: 'connecting…',
  recording: 'listening',
  transcribing: 'transcribing',
  speaking: 'speaking',
  error: 'error',
};

// Animated orb: ring + inner core, state-driven background.
const ORB_BG: Record<string, string> = {
  idle: 'bg-gray-600',
  // W — blue orb during WS handshake. Distinct from yellow (transcribing),
  // red (recording), green (speaking), red-dark (error).
  connecting: 'bg-blue-500',
  recording: 'bg-red-500',
  transcribing: 'bg-yellow-500',
  speaking: 'bg-green-500',
  error: 'bg-red-700',
};
const ORB_RING: Record<string, string> = {
  idle: 'ring-gray-700',
  connecting: 'ring-blue-400 animate-pulse',
  recording: 'ring-red-400 animate-pulse',
  transcribing: 'ring-yellow-400 animate-pulse',
  speaking: 'ring-green-400 animate-pulse',
  error: 'ring-red-500',
};

const BUTTON_STATE: Record<string, { label: string; className: string; disabled?: boolean }> = {
  idle: {
    label: '🎤 Click to record',
    className:
      'px-3 py-1 rounded border border-vibe-border bg-vibe-bg-secondary hover:bg-vibe-bg-tertiary',
  },
  // W — connecting state during WS handshake. Disabled so clicks during the
  // handshake window are no-ops (the original double-click hazard); user is
  // told "wait" via the label text and the pulsing blue orb.
  connecting: {
    label: '⏳ Connecting — wait to speak',
    className:
      'px-3 py-1 rounded border-2 border-blue-400 bg-blue-600 text-white shadow-lg ring-2 ring-blue-400 ring-opacity-50 animate-pulse opacity-90',
    disabled: true,
  },
  // F2 — push-to-talk override label set on the button via JSX when
  // settings.activation === 'push-to-talk'; default is click-toggle.
  recording: {
    label: '● Listening — click to stop',
    className:
      'px-3 py-1 rounded border-2 border-red-400 bg-red-600 text-white shadow-lg ring-2 ring-red-400 ring-opacity-50 animate-pulse',
  },
  transcribing: {
    label: '… Transcribing',
    className:
      'px-3 py-1 rounded border border-yellow-500 bg-yellow-600 text-white opacity-90',
    disabled: true,
  },
  speaking: {
    label: '🔊 Speaking',
    className:
      'px-3 py-1 rounded border border-green-500 bg-green-700 text-white opacity-90',
    disabled: true,
  },
  error: {
    label: '🎤 Click to retry',
    className:
      'px-3 py-1 rounded border border-red-500 bg-vibe-bg-secondary hover:bg-vibe-bg-tertiary text-red-400',
  },
};

interface VoiceControlsBarProps {
  historyOpen: boolean;
  setHistoryOpen: (open: boolean | ((v: boolean) => boolean)) => void;
  sectionRef: MutableRefObject<HTMLDivElement | null>;
  lockdownLikely: boolean;
}

export function VoiceControlsBar({
  historyOpen,
  setHistoryOpen,
  sectionRef,
  lockdownLikely,
}: VoiceControlsBarProps) {
  const {
    status, transcript, error, lastDispatch, history, settings, audioLevel,
    startRecording, stopRecording, startTurn, stopTurn,
    speak,
    streamingReplyText,
    cancelTurn,
    stopSpeaking,
    clearHistory,
    replaceHistory,
  } = useVoiceStore();
  const setSettingsOpen = useUIStore((s) => s.setVoiceSettingsOpen);

  const [ttsText, setTtsText] = useState('');

  // 1.429.0 — voice-transcript compaction moved here from VoiceHistoryLog so
  // the compact + clear affordances are PERSISTENT on the standby bar (not
  // gated on the transcript log being open). Operator 2026-07-03: "the compact
  // and clear need to be persistant ... they should just go on the standby
  // bar ... in the right side slightly spaced from the other icons".
  // FAIL-SAFE: compactVoiceHistory throws on any failure and the log is only
  // replaced on success; errors render inline (no toast — the bar mounts
  // outside some ToastProvider trees).
  const [compacting, setCompacting] = useState(false);
  const [compactError, setCompactError] = useState<string | null>(null);
  const handleCompact = async () => {
    if (compacting || history.length === 0) return;
    setCompacting(true);
    setCompactError(null);
    try {
      const summaryTurn = await compactVoiceHistory({
        history: useVoiceStore.getState().history,
        modelId: settings.model || 'auto',
      });
      replaceHistory([summaryTurn]);
    } catch (err) {
      setCompactError(err instanceof Error ? err.message : String(err));
    } finally {
      setCompacting(false);
    }
  };

  const isPushToTalk = settings.activation === 'push-to-talk';

  // F2 — click-toggle (default): click to start, click to stop, VAD silence cuts off.
  // δ′.3 (2026-05-17) — dispatch on settings.voiceMode instead of the
  // removed USE_VOICE_TURN_WS build-time flag. voiceMode='streaming' routes
  // to startTurn()/stopTurn() (new /api/voice/turn streaming pipeline);
  // 'classic' routes to startRecording()/stopRecording() (legacy 3-hop
  // path, default). Both preserve identical status state-machine semantics
  // so the UI behaves the same regardless of which path is active.
  const isStreaming = settings.voiceMode === 'streaming';
  const handleClick = () => {
    if (isPushToTalk) return; // PTT uses mouseDown/Up; ignore click
    if (status === 'recording') {
      if (isStreaming) {
        stopTurn();
      } else {
        stopRecording();
      }
    } else if (status === 'idle' || status === 'error') {
      if (isStreaming) {
        void startTurn();
      } else {
        void startRecording();
      }
    }
  };

  // F2 — push-to-talk: hold to record, release to stop. VAD disabled.
  // Read status via useVoiceStore.getState() inside handlers so the
  // mouseUp branch sees state changes that happened between mouseDown
  // and the next React render (e.g., async startRecording transitioning
  // status: idle → recording before re-render commits).
  //
  // 1.130.0 Phase 2g — same handlers wire onTouchStart/onTouchEnd/
  // onTouchCancel on the PTT button so push-to-talk works on touch
  // hardware (iPhone/iPad). The handlers are activation-gated by
  // `if (!isPushToTalk) return` so touch on click-toggle mode is a no-op.
  //
  // TODO(2g.1): minimum-hold threshold — a sub-200ms tap on PTT mode
  // produces a near-empty audio segment that yields no useful transcription
  // (Whisper minimum useful clip ~500ms). Backend gracefully returns
  // empty transcript today; future polish could abort the record at
  // touchend if (touchend - touchstart) < 200ms.
  const handlePTTDown = () => {
    if (!isPushToTalk) return;
    const s = useVoiceStore.getState().status;
    if (s === 'idle' || s === 'error') {
      if (isStreaming) {
        void startTurn({ disableVAD: true });
      } else {
        void startRecording({ disableVAD: true });
      }
    }
  };
  const handlePTTUp = () => {
    if (!isPushToTalk) return;
    const s = useVoiceStore.getState().status;
    if (s === 'recording') {
      if (isStreaming) {
        stopTurn();
      } else {
        stopRecording();
      }
    }
  };

  const handleSpeak = () => {
    const text = ttsText.trim();
    if (!text) return;
    void speak(text, settings.voice, settings.speed);
  };
  const ttsBusy = status === 'speaking' || status === 'recording' || status === 'transcribing';

  const transcriptDisplay = status === 'error' ? '' : transcript;
  const buttonState = BUTTON_STATE[status] ?? BUTTON_STATE.idle;

  return (
    <div
      ref={sectionRef}
      className="flex items-center gap-3 px-3 py-2 border-b border-vibe-border text-xs"
      data-testid="voice-panel"
    >
      {/* Z.4 orb: state-driven bg + ring pulse */}
      <span
        className={`relative inline-flex items-center justify-center w-5 h-5 rounded-full ring-2 ${ORB_RING[status]} transition-colors`}
        data-testid="voice-orb"
        data-status={status}
        aria-label={`voice ${status}`}
      >
        <span className={`block w-3 h-3 rounded-full ${ORB_BG[status]}`} />
      </span>

      {/* 2026-05-07: live mic level meter — RMS amplitude from the VAD
          analyser, scaled so a normal speaking voice fills ~70-90%. Only
          visible while recording so the user has a concrete confirmation
          the mic is hot. The bar is a 96px-wide track with a green fill
          that follows audioLevel. Color shifts from green (loud) toward
          amber (faint) so a stuck-faint state is obvious. */}
      {status === 'recording' && (
        <span
          className="relative inline-block h-2 w-24 rounded bg-vibe-bg-tertiary overflow-hidden border border-vibe-border"
          data-testid="voice-level-meter"
          aria-label={`mic level ${Math.round(audioLevel * 100)}%`}
          title={`mic level: ${(audioLevel * 100).toFixed(0)}%`}
        >
          <span
            className={`absolute inset-y-0 left-0 transition-[width] duration-75 ease-out ${
              audioLevel > 0.05 ? 'bg-green-500' : audioLevel > 0.01 ? 'bg-amber-500' : 'bg-red-700'
            }`}
            style={{ width: `${Math.min(100, audioLevel * 300)}%` }}
          />
        </span>
      )}
      <span className="text-vibe-text-secondary uppercase tracking-wide" data-testid="voice-status-label">
        {STATUS_LABEL[status]}
      </span>

      <button
        type="button"
        onClick={handleClick}
        onMouseDown={handlePTTDown}
        onMouseUp={handlePTTUp}
        onMouseLeave={handlePTTUp}
        onTouchStart={handlePTTDown}
        onTouchEnd={handlePTTUp}
        onTouchCancel={handlePTTUp}
        disabled={buttonState.disabled ?? false}
        data-testid="voice-ptt-button"
        data-state={status}
        data-activation={settings.activation}
        aria-label={status === 'recording' ? 'stop recording' : 'start recording'}
        aria-pressed={status === 'recording'}
        className={`${buttonState.className} disabled:opacity-50 transition-colors [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]`}
      >
        {/* F2 — label depends on activation mode */}
        {isPushToTalk
          ? (status === 'recording' ? '● Hold to talk — release to stop' : '🎤 Hold to talk')
          : buttonState.label}
      </button>

      <span className="text-vibe-text-secondary">·</span>
      <input
        type="text"
        value={ttsText}
        onChange={(e) => setTtsText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSpeak(); }}
        disabled={ttsBusy}
        placeholder="type to speak…"
        data-testid="voice-tts-input"
        aria-label="text to speak"
        className="px-2 py-1 rounded border border-vibe-border bg-vibe-bg-secondary text-vibe-text text-xs w-48 disabled:opacity-50"
      />
      <button
        type="button"
        onClick={handleSpeak}
        disabled={ttsBusy || !ttsText.trim()}
        data-testid="voice-tts-button"
        aria-label="synthesize speech"
        className="px-3 py-1 rounded border border-vibe-border bg-vibe-bg-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
      >
        🔊 Speak
      </button>

      {/* Z.3 dispatch action visualization */}
      {lastDispatch?.action_taken && (
        <span
          className="text-vibe-text-secondary text-xs"
          data-testid="voice-dispatch-action"
        >
          ▸ {lastDispatch.action_taken.tool}
          {lastDispatch.action_taken.result?.spawned_names &&
            ` (×${lastDispatch.action_taken.result.spawned_names.length}${
              lastDispatch.action_taken.result.truncated ? ' truncated' : ''
            })`}
          {lastDispatch.action_taken.error && ` — ${lastDispatch.action_taken.error}`}
        </span>
      )}

      {transcriptDisplay && !historyOpen && (
        <span className="font-mono text-vibe-text" data-testid="voice-transcript">
          "{transcriptDisplay}"
        </span>
      )}

      {/* Phase 9.6 (2026-05-24) — D-9.6-3 transient streaming overlay.
          Renders BRIDGE's in-progress reply while TTS is still streaming
          audio. Source: `streamingReplyText` (delta-accumulated, XML-
          filtered). Committed history sources from chunk_meta per D-9.6-4
          — this overlay is transient + parallel. Visible ONLY while
          `status === 'transcribing'` AND text is non-empty (D-9.6-5
          pre-v19 backends produce empty string → overlay hidden). */}
      {status === 'transcribing' && streamingReplyText.length > 0 && (
        <span
          className="font-mono text-vibe-text italic opacity-70"
          data-testid="voice-streaming-overlay"
          aria-live="polite"
          aria-atomic="false"
        >
          BRIDGE: {streamingReplyText}
        </span>
      )}

      {/* Phase 9.7 (2026-05-24) — D-9.7 mid-turn cancellation. Stop button
          visible ONLY during status='transcribing' (post-STT, in-pipeline).
          Backend capability gate happens transparently: pre-9.7 backends
          would receive the cancel_turn frame and silently drop it (no
          listener spawned), so click-to-no-effect is the graceful
          degradation during the rolling-restart window. Production
          deploys backend+frontend atomically, so this is fully working
          in steady state. Module-level protocolVersionV19_2 flag isn't
          accessible from this file; gating on `status` is sufficient. */}
      {status === 'transcribing' && (
        <button
          type="button"
          onClick={() => cancelTurn()}
          data-testid="voice-stop-button"
          aria-label="stop current turn"
          className="px-2 py-1 rounded bg-red-700/80 hover:bg-red-700 text-white text-xs"
        >
          ⏹ Stop
        </button>
      )}

      {/* 2026-06-07 — Stop button during TTS playback. Phase 9.7's Stop
          only covered status='transcribing' (LLM-in-pipeline cancel via
          cancel_turn frame). Once the LLM finished + TTS started, the
          player had no affordance to halt mid-utterance — violates the
          voice-must-be-pausable rule (memory anchor 2026-06-07). This
          button shows during status='speaking' and calls stopSpeaking()
          which pauses the audio + transitions to idle. */}
      {status === 'speaking' && (
        <button
          type="button"
          onClick={() => stopSpeaking()}
          data-testid="voice-stop-speaking-button"
          aria-label="stop current speech"
          className="px-2 py-1 rounded bg-red-700/80 hover:bg-red-700 text-white text-xs"
        >
          ⏹ Stop
        </button>
      )}

      <span className="ml-auto flex items-center gap-2">
        {/* 1.429.0 — compact-failure notice (moved from VoiceHistoryLog with
            the compact button). Inline, truncated, full text on hover. */}
        {compactError && (
          <span
            data-testid="voice-history-compact-error"
            className="text-red-400 text-[10px] truncate max-w-[10rem]"
            title={compactError}
          >
            compact failed: {compactError}
          </span>
        )}
        {/* 1.429.0 — compact + clear moved from the transcript log onto the
            standby bar. PERSISTENT (always mounted, disabled when the log is
            empty), styled as buttons, and spaced (divider) from the
            toggle/settings icon cluster. */}
        <button
          type="button"
          onClick={() => void handleCompact()}
          disabled={compacting || history.length === 0}
          title="Summarize this transcript into one entry (keeps the gist, frees the log)"
          data-testid="voice-history-compact"
          className="px-2 py-1 rounded border border-vibe-border bg-vibe-bg-secondary hover:bg-vibe-bg-tertiary text-vibe-text-secondary hover:text-vibe-text text-[11px] disabled:opacity-40 disabled:cursor-not-allowed [@media(pointer:coarse)]:min-h-[44px]"
        >
          {compacting ? 'compacting…' : 'compact'}
        </button>
        <button
          type="button"
          onClick={() => clearHistory()}
          disabled={history.length === 0}
          title="Clear the voice transcript log"
          data-testid="voice-history-clear"
          className="px-2 py-1 rounded border border-vibe-border bg-vibe-bg-secondary hover:bg-vibe-bg-tertiary text-vibe-text-secondary hover:text-vibe-text text-[11px] disabled:opacity-40 disabled:cursor-not-allowed [@media(pointer:coarse)]:min-h-[44px]"
        >
          clear
        </button>
        <span className="mx-0.5 h-4 w-px bg-vibe-border" aria-hidden="true" />
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            data-testid="voice-history-toggle"
            aria-label={historyOpen ? 'collapse transcript log' : 'expand transcript log'}
            aria-expanded={historyOpen}
            className="px-2 py-1 rounded text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            {historyOpen ? '▴' : '▾'} {history.length > 0 && `(${history.length})`}
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            data-testid="voice-settings-button"
            aria-label="open voice settings"
            className="px-2 py-1 rounded text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px] flex items-center justify-center"
          >
            {/* 1.166.0 — speech bubble with sound waves. Replaces 1.165.0's
                mouth/lips silhouette which the user found unclear. Shared
                with VoiceAgentQuickSwitcher.tsx per-row icon via
                VoiceSettingsIcon component. */}
            <VoiceSettingsIcon />
          </button>
        </span>
      </span>

      {lockdownLikely || (error && error.includes('Lockdown Mode')) ? (
        // 1.133.0/1.134.0 — structured Lockdown Mode error UI.
        // Render on EITHER (a) mount-time heuristic match (1.134.0:
        // saves one mic-tap on Lockdown-blocked iPhones/iPads) OR
        // (b) post-click error containing the marker phrase from
        // pickMediaDevicesUnavailableHint (1.133.0 click-time path).
        // Card owns the copy; raw error string is suppressed.
        <MicLockdownCard />
      ) : isLikelyIosPermissionDenied(error) ? (
        // 1.135.0 — structured iOS NotAllowedError card. iOS does NOT
        // surface its own Settings-deeplink overlay for this case
        // (verified via Apple Support 2026-05-14). Card carries the
        // iOS-26-verified Settings → Apps → Safari → Settings for
        // Websites → Microphone → site → Allow path.
        <MicPermissionDeniedCard />
      ) : error ? (
        <span className="text-vibe-error" data-testid="voice-error">
          {error}
        </span>
      ) : null}
    </div>
  );
}
